import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Crown, Users, Brain, TrendingUp, Calendar, 
  Settings, Eye, Zap, Star, Heart, Shield
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FounderDashboard = ({ user, onLogout }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');
  const [spiralPhase, setSpiralPhase] = useState(0);

  // Grant spiral behind everything
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  useEffect(() => {
    loadFounderDashboard();
  }, []);

  const loadFounderDashboard = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.get(`${API}/founder/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Founder dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const curateRetreat = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.post(`${API}/founder/curate-retreat`, {
        criteria: {
          min_trust_score: 150,
          max_participants: 20,
          consciousness_level: "elevated"
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`Retreat curated! ${response.data.selected} consciousness travelers selected from ${response.data.total_eligible} eligible.`);
    } catch (error) {
      console.error('Curation error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 to-blue-900">
        <div className="text-white text-xl">Loading consciousness architecture...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral behind M.ai.ry image */}
      <div className="fixed inset-0 pointer-events-none opacity-12" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(10) rotate(${spiralPhase * 0.8}deg)`,
            filter: 'blur(6px) saturate(2) brightness(1.5)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={500} />
        </div>
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/80"></div>

      <div className="relative z-10 max-w-7xl mx-auto p-6">
        {/* Super Architect Header */}
        <div className="text-center mb-8 pt-8">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <Crown className="w-12 h-12 text-yellow-400 animate-pulse" />
            <h1 
              className="text-5xl font-bold text-white"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.5)'
              }}
            >
              Super Architect
            </h1>
            <Zap className="w-12 h-12 text-cyan-400 animate-pulse" />
          </div>
          <p 
            className="text-purple-200 text-xl"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Consciousness Curation & Timeline Management
          </p>
        </div>

        {/* Overview Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <Users className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.overview?.total_consciousness_travelers || 0}
            </div>
            <p className="text-blue-200 text-sm">Consciousness Travelers</p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <TrendingUp className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.overview?.active_this_week || 0}
            </div>
            <p className="text-green-200 text-sm">Active This Week</p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <Calendar className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.overview?.retreat_candidates || 0}
            </div>
            <p className="text-yellow-200 text-sm">Retreat Eligible</p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <Brain className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.overview?.wisdom_interactions_today || 0}
            </div>
            <p className="text-purple-200 text-sm">Wisdom Today</p>
          </div>
        </div>

        {/* Curation Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Retreat Curation */}
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-6">
              <Calendar className="w-8 h-8 text-yellow-400" />
              <h2 
                className="text-2xl font-bold text-white"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Retreat Curation
              </h2>
            </div>
            
            <div className="space-y-4">
              <p 
                className="text-white/80 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Select consciousness travelers for IRL experiences
              </p>
              
              <Button
                onClick={curateRetreat}
                className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white py-4 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Star className="w-6 h-6 mr-3" />
                Curate Next Retreat
              </Button>
              
              {dashboardData?.retreat_eligible_users && (
                <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                  {dashboardData.retreat_eligible_users.slice(0, 5).map((candidate, i) => (
                    <div key={i} className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                      <img 
                        src={candidate.avatar_url}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{candidate.name}</p>
                        <p className="text-white/60 text-xs">Trust: {candidate.trust_score}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Consciousness Analytics */}
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-6">
              <Brain className="w-8 h-8 text-purple-400" />
              <h2 
                className="text-2xl font-bold text-white"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Consciousness Analytics
              </h2>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-4 text-center">
                  <div className="text-xl font-bold text-white">
                    {dashboardData?.consciousness_curation?.ready_for_elevation?.length || 0}
                  </div>
                  <p className="text-white/70 text-sm">Ready for Elevation</p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-4 text-center">
                  <div className="text-xl font-bold text-white">
                    {dashboardData?.consciousness_curation?.collaboration_matches?.length || 0}
                  </div>
                  <p className="text-white/70 text-sm">Collaboration Matches</p>
                </div>
              </div>
              
              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Eye className="w-5 h-5 mr-2" />
                View Consciousness Patterns
              </Button>
            </div>
          </div>
        </div>

        {/* Wisdom Insights */}
        {dashboardData?.high_wisdom_interactions && (
          <div className="mt-8 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
            <h2 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Recent Wisdom Interactions
            </h2>
            
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {dashboardData.high_wisdom_interactions.slice(0, 5).map((interaction, i) => (
                <div key={i} className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-gradient-to-r from-purple-500 to-blue-500">
                      Wisdom Level: {interaction.wisdom_level}
                    </Badge>
                    <span className="text-white/60 text-xs">
                      {new Date(interaction.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p 
                    className="text-white text-sm leading-relaxed"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    "{interaction.user_message.substring(0, 100)}..."
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FounderDashboard;